import React, { useState, useEffect, useCallback } from 'react';
import {
  DollarSign, Plus, Pencil, Trash2, Calendar, FileText, Upload, X,
  ChevronDown, ChevronUp, Image, CreditCard, Banknote, Building2
} from 'lucide-react';
import historicalPaymentService from '../../services/historicalPaymentService';
import api from '../../services/api';
import caseLabel from '../../utils/caseLabel';

const PAYMENT_METHODS = [
  { value: 'check', label: 'Check', icon: FileText },
  { value: 'cash', label: 'Cash', icon: Banknote },
  { value: 'wire', label: 'Wire Transfer', icon: Building2 },
  { value: 'square', label: 'Square/Card', icon: CreditCard },
  { value: 'other', label: 'Other', icon: DollarSign }
];

const PAYMENT_CATEGORIES = [
  { value: 'retainer', label: 'Retainer' },
  { value: 'legal_fees', label: 'Legal Fees' },
  { value: 'court_costs', label: 'Court Costs' },
  { value: 'expenses', label: 'Expenses' },
  { value: 'other', label: 'Other' }
];

function HistoricalPayments({ contactId }) {
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [total, setTotal] = useState(0);
  const [expanded, setExpanded] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [cases, setCases] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  const [formData, setFormData] = useState({
    payment_date: new Date().toISOString().split('T')[0],
    amount: '',
    payment_method: 'check',
    reference_number: '',
    payer_name: '',
    category: '',
    notes: '',
    case_id: '',
    invoice_id: ''
  });

  useEffect(() => {
    if (contactId) {
      fetchPayments();
      fetchCases();
      fetchInvoices();
    }
  }, [contactId]);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const response = await historicalPaymentService.getPayments({ contact_id: contactId });
      setPayments(response.payments || []);
      setTotalAmount(response.total_amount || 0);
      setTotal(response.total || 0);
    } catch (err) {
      console.error('Failed to fetch historical payments:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCases = async () => {
    try {
      const response = await api.get(`/cases/?contact_id=${contactId}&page_size=100`);
      setCases(response.data?.cases || []);
    } catch (err) {
      console.error('Failed to fetch cases:', err);
    }
  };

  const fetchInvoices = async () => {
    try {
      const response = await api.get(`/invoices/?contact_id=${contactId}&page_size=100`);
      setInvoices(response.data?.invoices || []);
    } catch (err) {
      console.error('Failed to fetch invoices:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        contact_id: contactId,
        amount: parseFloat(formData.amount),
        payment_date: new Date(formData.payment_date).toISOString(),
        case_id: formData.case_id || null,
        invoice_id: formData.invoice_id || null
      };

      let savedPayment;
      if (editingPayment) {
        savedPayment = await historicalPaymentService.updatePayment(editingPayment.id, payload);
      } else {
        savedPayment = await historicalPaymentService.createPayment(payload);
      }

      // Upload check image if selected
      if (selectedFile && savedPayment?.id) {
        await historicalPaymentService.uploadCheckImage(savedPayment.id, selectedFile);
      }

      resetForm();
      fetchPayments();
    } catch (err) {
      console.error('Failed to save payment:', err);
      alert(err.response?.data?.detail || 'Failed to save payment');
    }
  };

  const handleEdit = (payment) => {
    setEditingPayment(payment);
    setFormData({
      payment_date: payment.payment_date.split('T')[0],
      amount: payment.amount.toString(),
      payment_method: payment.payment_method,
      reference_number: payment.reference_number || '',
      payer_name: payment.payer_name || '',
      category: payment.category || '',
      notes: payment.notes || '',
      case_id: payment.case_id || '',
      invoice_id: payment.invoice_id || ''
    });
    if (payment.check_image_filename) {
      historicalPaymentService.getCheckImageBlobUrl(payment.id)
        .then(setImagePreview)
        .catch((err) => console.error('Failed to load check image:', err));
    }
    setShowModal(true);
  };

  const handleDelete = async (paymentId) => {
    if (!window.confirm('Are you sure you want to delete this payment?')) return;
    try {
      await historicalPaymentService.deletePayment(paymentId);
      fetchPayments();
    } catch (err) {
      console.error('Failed to delete payment:', err);
      alert(err.response?.data?.detail || 'Failed to delete payment');
    }
  };

  const resetForm = () => {
    setShowModal(false);
    setEditingPayment(null);
    setSelectedFile(null);
    setImagePreview(null);
    setFormData({
      payment_date: new Date().toISOString().split('T')[0],
      amount: '',
      payment_method: 'check',
      reference_number: '',
      payer_name: '',
      category: '',
      notes: '',
      case_id: '',
      invoice_id: ''
    });
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  }, []);

  const handleFileSelect = (file) => {
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      alert('Please select a PNG, JPEG, or PDF file');
      return;
    }
    setSelectedFile(file);
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setImagePreview(null);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getMethodIcon = (method) => {
    const methodConfig = PAYMENT_METHODS.find(m => m.value === method);
    const IconComponent = methodConfig?.icon || DollarSign;
    return <IconComponent className="w-4 h-4" />;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-virgo-teal-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-2 text-virgo-teal-200 hover:text-virgo-teal-100"
        >
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          <span className="font-medium">
            {total} payment{total !== 1 ? 's' : ''} totaling {formatCurrency(totalAmount)}
          </span>
        </button>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-3 py-1.5 bg-virgo-teal-600 text-white rounded-lg hover:bg-virgo-teal-700 transition text-sm"
        >
          <Plus className="w-4 h-4" />
          Add Payment
        </button>
      </div>

      {/* Payments List */}
      {expanded && payments.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-virgo-teal-400 border-b border-virgo-navy-600">
                <th className="text-left py-2 px-2">Date</th>
                <th className="text-left py-2 px-2">Amount</th>
                <th className="text-left py-2 px-2">Method</th>
                <th className="text-left py-2 px-2">Case</th>
                <th className="text-left py-2 px-2">Reference</th>
                <th className="text-left py-2 px-2">Check</th>
                <th className="text-right py-2 px-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((payment) => (
                <tr key={payment.id} className="border-b border-virgo-navy-700 hover:bg-virgo-navy-700/50">
                  <td className="py-2 px-2 text-virgo-teal-100">{formatDate(payment.payment_date)}</td>
                  <td className="py-2 px-2 text-virgo-teal-100 font-medium">{formatCurrency(payment.amount)}</td>
                  <td className="py-2 px-2">
                    <span className="flex items-center gap-1 text-virgo-teal-300">
                      {getMethodIcon(payment.payment_method)}
                      <span className="capitalize">{payment.payment_method?.replace('_', ' ')}</span>
                    </span>
                  </td>
                  <td className="py-2 px-2 text-virgo-teal-300">{payment.case_name || '-'}</td>
                  <td className="py-2 px-2 text-virgo-teal-300">{payment.reference_number || '-'}</td>
                  <td className="py-2 px-2">
                    {payment.check_image_filename ? (
                      <button
                        type="button"
                        onClick={() => historicalPaymentService.openCheckImage(payment.id)}
                        className="text-virgo-teal-400 hover:text-virgo-teal-300"
                        title="Open check image"
                      >
                        <Image className="w-4 h-4" />
                      </button>
                    ) : (
                      <span className="text-virgo-navy-500">-</span>
                    )}
                  </td>
                  <td className="py-2 px-2 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleEdit(payment)}
                        className="text-virgo-teal-400 hover:text-virgo-teal-300"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(payment.id)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {expanded && payments.length === 0 && (
        <div className="text-center py-8 text-virgo-teal-400">
          No historical payments recorded for this client.
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-virgo-navy-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-virgo-navy-700">
              <h3 className="text-lg font-semibold text-virgo-teal-100">
                {editingPayment ? 'Edit Payment' : 'Add Historical Payment'}
              </h3>
              <button onClick={resetForm} className="text-virgo-teal-400 hover:text-virgo-teal-300">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              {/* Row 1: Date and Amount */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-virgo-teal-300 mb-1">Payment Date *</label>
                  <input
                    type="date"
                    value={formData.payment_date}
                    onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })}
                    required
                    className="w-full px-3 py-2 bg-virgo-navy-700 border border-virgo-navy-600 rounded-lg text-virgo-teal-100 focus:outline-none focus:border-virgo-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-virgo-teal-300 mb-1">Amount *</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-virgo-teal-400">$</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      required
                      placeholder="0.00"
                      className="w-full pl-7 pr-3 py-2 bg-virgo-navy-700 border border-virgo-navy-600 rounded-lg text-virgo-teal-100 focus:outline-none focus:border-virgo-teal-500"
                    />
                  </div>
                </div>
              </div>

              {/* Row 2: Payment Method and Category */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-virgo-teal-300 mb-1">Payment Method *</label>
                  <select
                    value={formData.payment_method}
                    onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                    required
                    className="w-full px-3 py-2 bg-virgo-navy-700 border border-virgo-navy-600 rounded-lg text-virgo-teal-100 focus:outline-none focus:border-virgo-teal-500"
                  >
                    {PAYMENT_METHODS.map((method) => (
                      <option key={method.value} value={method.value}>{method.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-virgo-teal-300 mb-1">Category</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-3 py-2 bg-virgo-navy-700 border border-virgo-navy-600 rounded-lg text-virgo-teal-100 focus:outline-none focus:border-virgo-teal-500"
                  >
                    <option value="">Select category...</option>
                    {PAYMENT_CATEGORIES.map((cat) => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Row 3: Payer Name and Reference */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-virgo-teal-300 mb-1">Payer Name</label>
                  <input
                    type="text"
                    value={formData.payer_name}
                    onChange={(e) => setFormData({ ...formData, payer_name: e.target.value })}
                    placeholder="Who made the payment"
                    className="w-full px-3 py-2 bg-virgo-navy-700 border border-virgo-navy-600 rounded-lg text-virgo-teal-100 focus:outline-none focus:border-virgo-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-virgo-teal-300 mb-1">Reference Number</label>
                  <input
                    type="text"
                    value={formData.reference_number}
                    onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })}
                    placeholder="Check #, wire confirmation, etc."
                    className="w-full px-3 py-2 bg-virgo-navy-700 border border-virgo-navy-600 rounded-lg text-virgo-teal-100 focus:outline-none focus:border-virgo-teal-500"
                  />
                </div>
              </div>

              {/* Row 4: Case and Invoice */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-virgo-teal-300 mb-1">Case/Matter</label>
                  <select
                    value={formData.case_id}
                    onChange={(e) => setFormData({ ...formData, case_id: e.target.value })}
                    className="w-full px-3 py-2 bg-virgo-navy-700 border border-virgo-navy-600 rounded-lg text-virgo-teal-100 focus:outline-none focus:border-virgo-teal-500"
                  >
                    <option value="">No case linked</option>
                    {cases.map((c) => (
                      <option key={c.id} value={c.id}>{caseLabel(c)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-virgo-teal-300 mb-1">Link to Invoice</label>
                  <select
                    value={formData.invoice_id}
                    onChange={(e) => setFormData({ ...formData, invoice_id: e.target.value })}
                    className="w-full px-3 py-2 bg-virgo-navy-700 border border-virgo-navy-600 rounded-lg text-virgo-teal-100 focus:outline-none focus:border-virgo-teal-500"
                  >
                    <option value="">No invoice linked</option>
                    {invoices.map((inv) => (
                      <option key={inv.id} value={inv.id}>
                        {inv.invoice_number} - {formatCurrency(inv.total_amount)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm text-virgo-teal-300 mb-1">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={2}
                  placeholder="Additional notes about this payment..."
                  className="w-full px-3 py-2 bg-virgo-navy-700 border border-virgo-navy-600 rounded-lg text-virgo-teal-100 focus:outline-none focus:border-virgo-teal-500"
                />
              </div>

              {/* Check Image Upload */}
              <div>
                <label className="block text-sm text-virgo-teal-300 mb-1">Check Image</label>
                <div
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  className={`border-2 border-dashed rounded-lg p-4 text-center transition ${
                    dragActive
                      ? 'border-virgo-teal-500 bg-virgo-navy-700'
                      : 'border-virgo-navy-600 hover:border-virgo-navy-500'
                  }`}
                >
                  {imagePreview ? (
                    <div className="relative">
                      <img
                        src={imagePreview}
                        alt="Check preview"
                        className="max-h-32 mx-auto rounded"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setImagePreview(null);
                          setSelectedFile(null);
                        }}
                        className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1 -mt-2 -mr-2"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : selectedFile ? (
                    <div className="flex items-center justify-center gap-2 text-virgo-teal-300">
                      <FileText className="w-5 h-5" />
                      <span>{selectedFile.name}</span>
                      <button
                        type="button"
                        onClick={() => setSelectedFile(null)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-8 h-8 mx-auto text-virgo-teal-500 mb-2" />
                      <p className="text-virgo-teal-400 text-sm">
                        Drag & drop or{' '}
                        <label className="text-virgo-teal-300 cursor-pointer hover:underline">
                          browse
                          <input
                            type="file"
                            accept="image/png,image/jpeg,application/pdf"
                            onChange={(e) => e.target.files[0] && handleFileSelect(e.target.files[0])}
                            className="hidden"
                          />
                        </label>
                      </p>
                      <p className="text-virgo-navy-400 text-xs mt-1">PNG, JPEG, or PDF</p>
                    </>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-virgo-navy-700">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 bg-virgo-navy-600 text-virgo-teal-200 rounded-lg hover:bg-virgo-navy-500 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-virgo-teal-600 text-white rounded-lg hover:bg-virgo-teal-700 transition"
                >
                  {editingPayment ? 'Update Payment' : 'Add Payment'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default HistoricalPayments;
